/**
 * GoogleSignInButton — botón "Continuar con Google" para /login y /register.
 *
 * Comportamiento:
 * - Si VITE_GOOGLE_OAUTH_CLIENT_ID NO está configurado, renderiza null
 *   (el resto del auth sigue funcionando sin Google).
 * - Si está configurado, renderiza el botón oficial de Google con tema
 *   filled_black (encaja con paleta dark Nexum).
 * - Al éxito de Google: invoca `onIdToken(credential)` que la página
 *   conecta con AuthContext.googleSignIn.
 * - Al error de Google: invoca `onError(code)` con código i18n traducible.
 */
import { GoogleLogin, type CredentialResponse } from "@react-oauth/google";

import { t } from "@/i18n/es";

interface Props {
  onIdToken: (idToken: string) => void;
  onError: (code: string) => void;
}

const HAS_GOOGLE_CLIENT_ID = Boolean(
  import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID,
);

export function GoogleSignInButton({ onIdToken, onError }: Props) {
  if (!HAS_GOOGLE_CLIENT_ID) {
    // Sin client_id en .env, no renderizamos nada. La página sigue funcional
    // con el form email/password.
    return null;
  }

  function handleSuccess(resp: CredentialResponse) {
    if (!resp.credential) {
      onError("id_token_required");
      return;
    }
    onIdToken(resp.credential);
  }

  function handleError() {
    onError("google_token_invalid");
  }

  return (
    <div className="flex flex-col gap-3" data-testid="google-signin-section">
      <div className="flex justify-center">
        {/* El widget GIS usa width fijo (320px). Lo envolvemos en un contenedor
            responsive con overflow-hidden para que no arrastre la página en
            viewports estrechos (la red de seguridad overflow-x del layout lo
            contiene a nivel de página). */}
        <div className="w-full max-w-xs overflow-hidden">
          <GoogleLogin
            onSuccess={handleSuccess}
            onError={handleError}
            theme="filled_black"
            shape="rectangular"
            text="continue_with"
            width={320}
            useOneTap={false}
          />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border-subtle" />
        <span className="text-xs uppercase tracking-widest text-gray-mid">
          {t.auth.googleSeparator}
        </span>
        <div className="h-px flex-1 bg-border-subtle" />
      </div>
    </div>
  );
}
